import React, { useEffect, useState } from 'react';
import { StandardButton } from '@/styles/StandardButtons';
import { Label } from '@/components/ui/label';
import { colors } from '@/design-system/colors';
import { FileIcon, Trash2, Upload } from 'lucide-react';
import { format } from 'date-fns';
import { GenericFileUploadModal } from '@/components/shared/GenericFileUploadModal';
import { formatUserDisplayName } from '@/lib/user-display';
import { getFileCategoryLabel } from '@/lib/file-category';
import { takeAttachmentFailures } from '@/lib/entity-attachments';
import { ProtectedImage } from '@/components/shared/ProtectedImage';
import "@/design-system/typography.css";
import '@/design-system/forms.css';

export interface GenericAttachment {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file_url: string;
  created_at: string;
  created_by: string;
  created_by_user?: {
    display_name?: string | null;
  };
  /** Entity-specific extras stored with the attachment (e.g. a user document's type/expiry). */
  metadata?: Record<string, any>;
}

const IMAGE_EXTENSION = /\.(jpe?g|png|gif|webp|bmp|svg|avif)$/i;
const isImageFile = (type: string | undefined, name: string) => (type || '').startsWith('image/') || IMAGE_EXTENSION.test(name);

/**
 * The one thumbnail every attachment row shows: the image itself when it is
 * one (a placeholder icon otherwise), with the Delete button in the top-right
 * corner of the thumbnail. Pending files get a local preview of the bytes the
 * user just picked, so they see their attachment immediately -- before the
 * record it belongs to exists.
 */
function AttachmentThumbnail({ src, attachmentId, name, type, href, onDelete, deleteLabel }: {
  /** a local preview of a pending file (its own bytes) */
  src?: string | null;
  /** an uploaded file: shown through KrewUp's content route */
  attachmentId?: string;
  name: string;
  type?: string;
  href?: string;
  onDelete?: () => void;
  deleteLabel: string;
}) {
  const [broken, setBroken] = useState(false);
  const icon = <FileIcon className="h-6 w-6 text-muted-foreground" />;
  const isImage = isImageFile(type, name);
  const visual = attachmentId && isImage ? (
    <ProtectedImage kind="attachment" id={attachmentId} alt={name} className="h-full w-full object-cover" fallback={icon} />
  ) : !!src && !broken && isImage ? (
    <img src={src} alt={name} className="h-full w-full object-cover" onError={() => setBroken(true)} />
  ) : icon;
  return (
    <div className="relative h-14 w-14 flex-shrink-0">
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="flex h-full w-full items-center justify-center overflow-hidden rounded border-2 border-border bg-muted" title={name}>
          {visual}
        </a>
      ) : (
        <div className="flex h-full w-full items-center justify-center overflow-hidden rounded border-2 border-border bg-muted" title={name}>
          {visual}
        </div>
      )}
      {onDelete && (
        <button
          type="button"
          aria-label={deleteLabel}
          title={deleteLabel}
          onClick={onDelete}
          className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded border border-border bg-white text-foreground"
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.buttonTertiaryHover; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

/** A local preview URL for a not-yet-uploaded File, revoked when it goes away. */
function PendingThumbnail({ file, onRemove }: { file: File; onRemove?: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function' || !isImageFile(file.type, file.name)) return undefined;
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return <AttachmentThumbnail src={url} name={file.name} type={file.type} onDelete={onRemove} deleteLabel="Remove file" />;
}

export interface PendingFile extends File {
  // Extends the File interface for pending uploads
}

interface GenericAttachmentsSectionProps {
  attachments: GenericAttachment[];
  pendingFiles?: File[];
  onUpload: (files: File[]) => Promise<void> | void;
  onDelete?: (attachmentId: string) => Promise<unknown> | void;
  onRemovePending?: (index: number) => void;
  mode: 'create' | 'edit';
  disabled?: boolean;
  /** The project whose Drive "Browse Drive" shows (the form's own Project field). Omitted = the globally selected project; null = the record has no project (Browse Drive disabled). */
  projectId?: string | null;
}

export function GenericAttachmentsSection({
  attachments = [],
  pendingFiles = [],
  onUpload,
  onDelete,
  onRemovePending,
  mode,
  disabled = false,
  projectId,
}: GenericAttachmentsSectionProps) {
  
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileUpload = async (files: File[]) => {
    if (!files.length) return;

    setUploadNotice([]);
    try {
      await onUpload(files);
    } catch (error) {
      console.error('Failed to upload files:', error);
    }
    // files that could not be attached (a Drive file of another project, a refused or missing file...) say why, right here
    setUploadNotice(takeAttachmentFailures());
  };

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadNotice, setUploadNotice] = useState<string[]>([]);

  const handleDeleteFile = async (attachmentId: string) => {
    if (!onDelete) return;
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    try {
      await onDelete(attachmentId);
    } catch (error) {
      console.error('Failed to delete file:', error);
    }
  };

  const constructFileUrl = (file: GenericAttachment) => {
    // If file_url is already a full URL (signed URL), use it
    if (file.file_url && file.file_url.startsWith('http')) {
      return file.file_url;
    }
    // If file_url is empty or invalid, don't construct a public URL (buckets are private)
    // Return empty string to indicate no valid URL available
    return '';
  };

  return (
    <div className="grid gap-2 min-w-0">
      <Label className="font-bold">Attachments</Label>

      {(pendingFiles.length > 0 || attachments.length > 0) && (
      <div className="border-2 border-border rounded-md overflow-hidden min-w-0">
      {/* Pending Files (not yet uploaded) -- no border/rounded-md of its
          own: the outer wrapper above is the single visual box for both
          this list and Uploaded Files below, so the two never show a
          nested double border. */}
      {pendingFiles.length > 0 && (
        <div className="divide-y divide-border">
          {pendingFiles.map((file, index) => (
            <div key={`${file.name}-${index}`} className="p-2">
              <div className="flex items-start gap-3">
                <PendingThumbnail file={file} onRemove={onRemovePending ? () => onRemovePending(index) : undefined} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="text-body truncate min-w-0 flex-1" title={file.name}>{file.name}</div>
                    <span className="text-body-small-muted flex-shrink-0">(pending upload)</span>
                  </div>
                  <div className="mt-1 text-body-small-muted text-right">
                    <span>{formatFileSize(file.size)}</span>
                    <span> • </span>
                    <span>{file.type || 'file'}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Uploaded Files -- same unified box as Pending Files above; a top
          border only appears here when both lists are shown at once, as
          the single divider between the two groups. */}
      {attachments.length > 0 && (
        <div className={`divide-y divide-border${pendingFiles.length > 0 ? ' border-t-2 border-border' : ''}`}>
          {attachments.map((file) => (
            <div key={file.id} className="p-2">
              <div className="flex items-start gap-3">
                <AttachmentThumbnail
                  attachmentId={constructFileUrl(file) ? file.id : undefined}
                  name={file.file_name}
                  type={file.file_type}
                  href={constructFileUrl(file) || undefined}
                  onDelete={onDelete && mode === 'edit' ? () => handleDeleteFile(file.id) : undefined}
                  deleteLabel="Delete file"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    {constructFileUrl(file) ? (
                      <a
                        href={constructFileUrl(file)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-body hover:underline truncate min-w-0 flex-1"
                        title={file.file_name}
                      >
                        {file.file_name}
                      </a>
                    ) : (
                      <span className="text-body truncate min-w-0 flex-1" title={file.file_name}>
                        {file.file_name}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground text-right">
                    <span>{formatFileSize(file.file_size)}</span>
                    <span> • </span>
                    <span>{getFileCategoryLabel(file.file_type, file.file_name)}</span>
                    {file.created_by_user && (
                      <>
                        <span> • </span>
                        <span>{formatUserDisplayName(file.created_by_user)}</span>
                      </>
                    )}
                    <span> • </span>
                    <span>{format(new Date(file.created_at), 'MMM d, h:mm a')}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
      )}

      {/* Upload Button -- opens the generic Upload Files popup (drag & drop
          + Browse Files); no entity-specific upload logic lives here. */}
      <div className="flex items-center justify-start gap-2">
        <StandardButton
          customVariant="blue"
          type="button"
          className="gap-2"
          disabled={disabled}
          onClick={() => setIsUploadModalOpen(true)}
        >
          <Upload className="h-4 w-4" />
          Upload Files
        </StandardButton>
      </div>


      {uploadNotice.length > 0 && (
        <div role="alert" className="text-sm text-destructive whitespace-pre-line">{uploadNotice.join('\n')}</div>
      )}

      <GenericFileUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onConfirm={handleFileUpload}
        multiple
        driveProjectId={projectId}
      />
    </div>
  );
}
